import { useEffect, useRef } from "react"
import { useRouter } from "next/router"

/**
 * Query param on the test set page naming a test case to select on load, which
 * scrolls its row into view. Links from a run's test case drawer set it.
 */
export const TEST_CASE_DEEP_LINK_PARAM = "testCaseId"

/**
 * Selects the test case the URL names once the editor has loaded it, so a link
 * to a test case lands on its row. Selecting a row scrolls it into view, which
 * only happens for a rendered row, so filters that hide the linked case are
 * cleared first. Each linked id is applied once, so later clicks and filters
 * in the editor are left alone.
 */
export function useTestCaseDeepLink({
  isInitialized,
  testCaseIds,
  visibleTestCaseIds,
  selectTestCase,
  clearFilters,
}: {
  isInitialized: boolean
  /** Every test case in the test set. */
  testCaseIds: readonly string[]
  /** The test cases the active search and type filters leave rendered. */
  visibleTestCaseIds: readonly string[]
  selectTestCase: (testCaseId: string) => void
  clearFilters: () => void
}) {
  const router = useRouter()
  const linkedId = router.query[TEST_CASE_DEEP_LINK_PARAM]
  const testCaseId = typeof linkedId === "string" ? linkedId : null
  const appliedIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (
      !isInitialized ||
      !testCaseId ||
      appliedIdRef.current === testCaseId ||
      !testCaseIds.includes(testCaseId)
    ) {
      return
    }
    appliedIdRef.current = testCaseId
    if (!visibleTestCaseIds.includes(testCaseId)) {
      clearFilters()
    }
    selectTestCase(testCaseId)
  }, [
    clearFilters,
    isInitialized,
    selectTestCase,
    testCaseId,
    testCaseIds,
    visibleTestCaseIds,
  ])
}
